import { createContext, useContext, useMemo, type ReactNode } from 'react'
import { useLowZoomNodeChromeLod } from './canvasZoomLod'

export interface CanvasNodeChromeModeValue {
  lightweightImageNodeShell: boolean
  lightweightNodeChrome: boolean
}

const DEFAULT_CANVAS_NODE_CHROME_MODE: CanvasNodeChromeModeValue = {
  lightweightImageNodeShell: false,
  lightweightNodeChrome: false,
}

const CanvasNodeChromeModeContext = createContext<CanvasNodeChromeModeValue>(
  DEFAULT_CANVAS_NODE_CHROME_MODE,
)

/**
 * `lightweightNodeChrome` here is the *floor*, not the whole answer: the
 * `Lightweight idle node chrome` setting forces lightweight chrome at every
 * zoom, and on top of that the provider turns it on automatically once the
 * viewport is zoomed out past the LOD threshold (see `canvasZoomLod.ts`).
 *
 * Reading the zoom here rather than in the caller is deliberate — this provider
 * is a small component, so a threshold crossing re-renders it and the context
 * consumers, not the whole workspace page. `children` keeps its element
 * identity across those re-renders, so React bails out of everything else.
 *
 * Must be rendered under a `<ReactFlowProvider>`.
 */
export function CanvasNodeChromeModeProvider({
  children,
  lightweightImageNodeShell,
  lightweightNodeChrome,
}: {
  children: ReactNode
  lightweightImageNodeShell: boolean
  lightweightNodeChrome: boolean
}) {
  const lowZoom = useLowZoomNodeChromeLod()
  const lightweightNodeChromeResolved = lightweightNodeChrome || lowZoom

  const value = useMemo(
    () => ({ lightweightImageNodeShell, lightweightNodeChrome: lightweightNodeChromeResolved }),
    [lightweightImageNodeShell, lightweightNodeChromeResolved],
  )

  return (
    <CanvasNodeChromeModeContext.Provider value={value}>
      {children}
    </CanvasNodeChromeModeContext.Provider>
  )
}

export function useCanvasNodeChromeMode(): CanvasNodeChromeModeValue {
  return useContext(CanvasNodeChromeModeContext)
}
